

// components
import PageHeader from "../../../components/Page/PageHeader";

// hooks
import { useEffect, useState, MouseEvent } from "react"

// types
import SnackBar from "../../../components/Alerts/SnackBar";
import useMessage from "../../../hooks/useMessage";
import { useCancelDagRunEndpointJobsCancelPost, useGetJobsJobsGet } from "../../../api/generated/fastAPI";
import { TableData, TableHeader } from "../../../components/Table/DataTable";
import SearchBar from "../../../components/Filtering/SearchBar";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import { useNavigate } from "react-router-dom";
import { GetJobsJobsGetParams } from "../../../api/models";
import useDataParams from "../../../hooks/useDataParams";
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination";
import DeleteModal from "../../../components/Popups/DeleteModal";
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar";
import useDelete from "../../../hooks/endpoints/useDelete";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";

const jobHeaders: TableHeader[] = [
    {
        label: 'DAG ID',
        value: 'dag_id',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Status',
        value: 'state',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Result Name',
        value: 'name',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Run Type',
        value: 'run_type',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Executed By',
        value: 'username',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Execution Time (s)',
        value: 'execution_time',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Execution Date',
        value: 'execution_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Queued Date',
        value: 'queued_at',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Start Date',
        value: 'start_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'End Date',
        value: 'end_date',
        isShown: true,
        alignment: 'center',
    }
]

const defaultParams: GetJobsJobsGetParams = {
    dag_id: null,
    username: null,
    state: null,
    limit: 10,
    offset: 0, 
    execution_date_gte: null,
    execution_date_lte: null,
    start_date_gte: null,
    start_date_lte: null,
    end_date_gte: null,
    end_date_lte: null,
    queued_at_gte: null,
    queued_at_lte: null,
    sort_by: 'execution_date',
    sort_order: 'desc',   
}

const emptyParams = [
    'dag_id', 
    'execution_date_gte', 
    'execution_date_lte', 
    'start_date_gte', 
    'start_date_lte',
    'end_date_gte',
    'end_date_lte',
    'queued_at_gte',
    'queued_at_lte',
]

const defaultFilters: FilterBarType[] = [
    {
        label: 'Execution Date',
        param: 'execution_date_gte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'executed_at',
    },
    {
        label: 'Execution Date',
        param: 'execution_date_lte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'executed_at',
    },
    {
        label: 'Queued Date',
        param: 'queued_at_gte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'queued_at',
    },
    {
        label: 'Queued Date',
        param: 'queued_at_lte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'queued_at',
    },
    {
        label: 'Start Date',
        param: 'start_date_gte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'started_at',
    },
    {
        label: 'Start Date',
        param: 'start_date_lte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'started_at',
    },
    {
        label: 'End Date',
        param: 'end_date_gte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'ended_at',
    },
    {
        label: 'End Date',
        param: 'end_date_lte',
        type: 'date-range',
        isDefault: true,
        groupKey: 'ended_at',
    },
    {
        label: 'Username',
        param: 'username',
        type: 'users',
        isDefault: true,
    },
]

export interface JobConf {
    created_by: string;
    data_file_hash: string | null;
    dataset_hash: string | null;
    docker_image_hash: string | null;
    docker_image_name: string | null;
    docker_image_tag: string | null;
    name: string;
    owner: string;
    stages: any[];
    [key: string]: any,
}

export interface AirflowJob {
    id: number;
    dag_id: string;
    queued_at: string;
    execution_date: string;
    start_date: string;
    end_date: string;
    state: string;
    run_id: string;
    external_trigger: boolean;
    run_type: string;
    conf: JobConf;
    dag_final_state_report?: {
        dag_state?: string;
        [key: string]: any,
    };
    user: {
        id: number,
        username: string,
    }[];
}

export default function JobQueue() {

    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams,
        defaultFilters,
    })

    const {
        open,
        setOpen,
        message,
        setPopupMessage,
        isSuccess,
    } = useMessage();

    const navigate = useNavigate();
    const getJobsMutation = useGetJobsJobsGet(params as GetJobsJobsGetParams, {
        query: {
            refetchInterval: 5000,
        }
    });
    const [ jobs, setJobs ] = useState<AirflowJob[]>([]);
    const [ count, setCount ] = useState(0);
    const [ jobsData, setJobsData ] = useState<TableData[]>([]);
    const [ headers, setHeaders ] = useState<TableHeader[]>(jobHeaders);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);

    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);

    const cancelJobMutation = useCancelDagRunEndpointJobsCancelPost();
    
    const refetchJobs = () => {
        getJobsMutation.refetch();
    };

    const { deleteJobs } = useDelete({ setPopupMessage, refetchFn: refetchJobs });

    const cancelJobs = async (ids: { dag_id: string, run_id: string }[]) => {
        const promises = ids.map(async (id) => {
            const { dag_id, run_id } = id;
            return cancelJobMutation.mutateAsync({ params: { dag_id: encodeURIComponent(dag_id), run_id: encodeURIComponent(run_id) } });
        });

        try {
            await Promise.all(promises);
            setPopupMessage(`The selected ${ids.length > 1 ? 'jobs have' : 'job has'} been canceled successfully.`, true);
            refetchJobs();
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while canceling the selected ${ids.length > 1 ? 'jobs' : 'job'}. Please try again.`, false);
        }
    };

    useEffect(() => {
        if (getJobsMutation.data) {
            const data = getJobsMutation.data as unknown as { count: number, jobs: AirflowJob[] };
            setJobs(data.jobs);
            setCount(data.count);
        }
    }, [getJobsMutation.data]);



    const getResultRedirectLink = (job: AirflowJob, state: string) => {
        let link = '';

        const dag_id = job.dag_id;

        if (job.dag_final_state_report && state.toLocaleLowerCase().includes('success')) {
            if (job.dag_final_state_report.report && job.dag_final_state_report.report.all_created_resources) {
                const createdSources = job.dag_final_state_report.report.all_created_resources;
                if (dag_id.includes('eclean') && createdSources.dataset_hash && Array.isArray(createdSources.dataset_hash)) {
                    if (createdSources.dataset_hash.length > 0) {
                        link = `/datasets/${createdSources.dataset_hash[0]}`
                    }
                } else if (dag_id.includes('train') && createdSources.model_family_id && Array.isArray(createdSources.model_family_id)) {
                    if (createdSources.model_family_id.length > 0) {
                        link = `/models/${createdSources.model_family_id[0]}`
                    }
                } else if (dag_id.includes('predict') && createdSources.resultset_id && Array.isArray(createdSources.resultset_id)) {
                    if (createdSources.resultset_id.length > 0) {
                        link = `/resultsets/${createdSources.resultset_id[0]}`
                    }
                } else if (dag_id.includes('readacross') && createdSources.readacross_id && Array.isArray(createdSources.readacross_id)) {
                    if (createdSources.readacross_id.length > 0) {
                        link = `/readacross/${createdSources.readacross_id[0]}`
                    }
                } else if (dag_id.includes('visualization') && createdSources.dataset_visualization_hash && Array.isArray(createdSources.dataset_visualization_hash)) {
                    if (createdSources.dataset_visualization_hash.length > 0 && job.conf.data_input_specification && job.conf.data_input_specification.input_value.length > 0) {
                        link = `/resultsets/${job.conf.data_input_specification.input_value[0]}/visualization/${createdSources.dataset_visualization_hash[0]}`
                    }
                }
            }
        };


        return link;
        
    }




    useEffect(() => {
        if (jobs.length > 0) {
            let sortedJobs = jobs;
            sortedJobs.sort((a,b) => {
                if (a.execution_date < b.execution_date) {
                    return 1;
                } else if (a.execution_date > b.execution_date) {
                    return -1;
                } else {
                    return 0;
                }
            })
            const items = sortedJobs.map(job => {
                let difference: string | number = 'Not Available';

                if (job.end_date && job.execution_date) {
                    const executionSeconds = new Date(job.execution_date).getTime();
                    const endDateSeconds = new Date(job.end_date).getTime();

                    difference = Math.round(Math.abs(executionSeconds - endDateSeconds)/1000);

                }

                const state = job.dag_final_state_report && job.dag_final_state_report.dag_state ? job.dag_final_state_report.dag_state : job.state;
                const redirectLink = getResultRedirectLink(job, state);
                const username = job.user && job.user.length > 0 ? job.user[0].username : 'No Value';
                

                return ({
                    id: job.id,
                    fields: [
                        {
                            associatedColumn: 'dag_id',
                            value: job.dag_id,
                            alignment: 'left' as any,
                            type: 'text' as any,
                            action: (e: MouseEvent<HTMLButtonElement>) => { navigate(`/jobs/${job.id}`) }
                        },
                        {
                            associatedColumn: 'state',
                            value: job.dag_final_state_report && job.dag_final_state_report.dag_state ? job.dag_final_state_report.dag_state : job.state,
                            alignment: 'center',
                            type: 'status' as any,
                        },
                        {
                            associatedColumn: 'name',
                            value: job.dag_id.includes('visualization') && job.conf.fp_type ? (job.conf.fp_type as string).toUpperCase() : job.conf.name ? job.conf.name : 'No Value',
                            alignment: 'center' as any,
                            type: 'text' as any,
                            action: redirectLink ? (e:MouseEvent<HTMLButtonElement>) => {navigate(redirectLink)} : undefined,
                        },
                        {
                            associatedColumn: 'run_type',
                            value: job.run_type ? job.run_type : 'No Value',
                            alignment: 'center',
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'username',
                            value: username,
                            alignment: 'center',
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'execution_time',
                            value: difference.toString(),
                            alignment: 'center',
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'execution_date',
                            value: job.execution_date ? new Date(job.execution_date).toLocaleString('en-US', {timeZone: 'EST'}) : 'Not available',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'queued_at',
                            value: job.queued_at ? new Date(job.queued_at).toLocaleString('en-US', {timeZone: 'EST'}) : 'Not available',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'start_date',
                            value: job.start_date ? new Date(job.start_date).toLocaleString('en-US', {timeZone: 'EST'}) : 'Not available',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                        {
                            associatedColumn: 'end_date',
                            value: job.end_date ? new Date(job.end_date).toLocaleString('en-US', {timeZone: 'EST'}) : 'Not available',
                            alignment: 'center' as any,
                            type: 'text' as any,
                        },
                    ],
                    options: injectOptions(job, state),
                })
            });


            setJobsData(items);

        }

        //eslint-disable-next-line
    }, [jobs]);

    useEffect(() => {
        let options: DropdownOption[] = [];

        if (jobs.length > 0 && selectedItems.length > 0) {
            let ids: { dag_id: string, run_id: string }[] = [];

            selectedItems.forEach(item => {
                const matchingJob = jobs.find(job => job.id === item);
                if (matchingJob) {
                    ids.push({ dag_id: matchingJob.dag_id, run_id: matchingJob.run_id });
                }
            });

            if (ids.length > 0) {
                const deleteModalOption: DropdownOption = {
                    label: 'Delete',
                    btnComp: (
                        <DeleteModal 
                            items={ids.map(item => ({
                                type: 'job',
                                id: item,
                                name: item.dag_id,
                            }))}
                            cancelFn={() => setSelectedItems([])}
                            deleteFn={deleteJobs}
                        />
                    ),
                };

                options.push(deleteModalOption);
            }
        }

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, jobs]);

    const injectOptions = (job: AirflowJob, state: string) => {
            const { dag_id, run_id } = job;

            const progressStates = ['running', 'queued']

            let options: DropdownOption[] = [];
            
            if (progressStates.includes(state.toLocaleLowerCase())) {
                options.push({
                    label: 'Cancel Job',
                    action: async (e:any) => cancelJobs([{ dag_id, run_id }]),
                })
            }

            options.push({
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'job',
                            id: { dag_id, run_id },
                            name: dag_id,
                        }]}
                        deleteFn={deleteJobs}
                    />
                ),
            })

            return options;
        }


    return (
        <div className="p-10 h-screen overflow-hidden flex flex-col justify-start items-stretch gap-4">


            <PageHeader label='Job Queue'></PageHeader>

            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="dag_id"
                            placeholder="Search by DAG ID"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={headers} 
                            setHeaders={setHeaders} 
                            isDisabled={false}
                        />
                    
                        {selectedItems.length > 0 && (
                            <Dropdown 
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'file-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                {jobs.length > 0 && (
                    <FilterBar 
                        filters={filters}
                        setParams={setParams}
                        params={params}
                    />
                )}
            </div>

            <VirtualizedDataTable 
                paramValues={Object.entries(params).filter(entry => emptyParams.includes(entry[0]) && entry[1]).map(val => val[1])}
                isLoading={getJobsMutation.isLoading} 
                headers={headers} 
                tableData={jobsData}
                selection={{
                    selectedItems,
                    setSelectedItems
                }}
                sorting={{
                    sortableHeaders: ['start_date', 'end_date', 'execution_date', 'queued_at'],
                    sortHeaders: sort,
                    sortByParam: params.sort_by!,
                    sortOrderParam: params.sort_order!,
                }}
                setPopupMessage={setPopupMessage}
                fixedItemHeight={69}
                dataType="jobs"
            />

                <UpdatedPagination 
                    count={count}
                    limit={params.limit!}
                    offset={params.offset!}
                    setPaginationLimit={setPaginationLimit}
                    setPaginationOffset={setPaginationOffset}
                    size="reg"
                    isLoading={false}
                />
            
            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
           
    
        </div>
    )
}